import { isPostalCodeExcludedCountry } from "@equidefi/constants/countries";
import * as Yup from "yup";
import { z } from "zod";

/**
 * Postal-code validation message for countries that don't use one.
 */

const DEFAULT_MESSAGE = "Postal code is required";

/**
 * Yup schema for a postal code that is required unless the `countryField` sibling is an
 * excluded country. For use in `Yup.object().shape({ ... })`.
 */
export const postalCodeYupSchema = (countryField: string, message: string = DEFAULT_MESSAGE) =>
  Yup.string()
    .nullable()
    .when(countryField, {
      is: (code: string) => !isPostalCodeExcludedCountry(code),
      then: Yup.string().nullable().required(message),
      otherwise: Yup.string().nullable(),
    });

interface PostalCodeRefinementOptions {
  /** Field holding the country code (or an already-normalized ISO code). */
  countryField: string;
  /** Field holding the postal code. */
  postalField: string;
  message?: string;
  /** Only enforce when this predicate is true (e.g. entity/trust branches). */
  when?: (data: Record<string, unknown>) => boolean;
}

/**
 * Zod `superRefine` callback that requires `postalField` unless `countryField` is an
 * excluded country. Make the postal field optional on the object, then pass this to
 * `.superRefine(...)`.
 *
 * @example
 * const schema = z
 *   .object({
 *     address_country_code: z.string().min(1),
 *     address_postal_code: z.string().optional().nullable(),
 *   })
 *   .superRefine(
 *     postalCodeZodRefinement({
 *       countryField: "address_country_code",
 *       postalField: "address_postal_code",
 *     }),
 *   );
 */
export const postalCodeZodRefinement =
  ({ countryField, postalField, message = DEFAULT_MESSAGE, when }: PostalCodeRefinementOptions) =>
  (data: Record<string, unknown>, ctx: z.RefinementCtx) => {
    if (when && !when(data)) return;

    const country = data[countryField] as string | null | undefined;
    const postal = data[postalField] as string | null | undefined;

    if (!isPostalCodeExcludedCountry(country) && !postal?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: [postalField],
      });
    }
  };
